import { Card, Form, Input, Button, Space, Message, Grid, Select, InputNumber, Table, Typography, Divider } from "@arco-design/web-react";
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ItemAPI } from "@/apis/material";
import { BOMAPI } from "@/apis/manufacturing";
import { IconPlus, IconDelete } from "@arco-design/web-react/icon";
import { Status } from "@/enums/status";

const FormItem = Form.Item;
const { Row, Col } = Grid;
const { Title } = Typography;

export const BillOfMaterialEdit = () => {
    const params = useParams();
    const bomId = params.bomId;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState([]);

    useEffect(() => {
        const fetchItems = async () => {
            const result = await ItemAPI.actives();
            setItems(result);
        };
        fetchItems();
    }, []);

    const getInfo = useCallback(async () => {
        try {
            const values = await BOMAPI.get(bomId);
            const finishedItems = (values.finishedItems || []).map(item => ({
                ...item,
                itemId: item.itemId,
            }));
            const rawItems = (values.rawItems || []).map(item => ({
                ...item,
                itemId: item.itemId,
            }));
            form.setFieldsValue({
                id: values.id,
                bomName: values.bomName,
                description: values.description,
                wasteFactor: values.wasteFactor,
                status: values.status,
                dateCreated: values.createTime,
                notes: values.notes,
                finishedItems,
                rawItems,
            });
        } catch (error) {
            Message.error(error.message);
        }
    }, [bomId, form]);

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    const onItemSelected = (val, index, field) => {
        const item = items.find(i => i.id === val);
        if (item) {
            form.setFieldValue(`${field}[${index}].description`, item.description);
            form.setFieldValue(`${field}[${index}].uom`, item.uom);
        }
    };

    const onSubmit = async () => {
        try {
            const values = await form.validate();
            if (!values.finishedItems || values.finishedItems.length === 0) {
                Message.warning("Finished items cannot be empty");
                return;
            }
            if (!values.rawItems || values.rawItems.length === 0) {
                Message.warning("Raw items cannot be empty");
                return;
            }
            const totalAllocation = (values.finishedItems || []).reduce((sum, item) => sum + (item.valueAllocation || 0), 0);
            if (totalAllocation !== 100) {
                Message.warning("Total value allocation must equal 100%");
                return;
            }
            setLoading(true);
            await BOMAPI.update(bomId, values);
            Message.success("BOM updated successfully");
            navigate(`/workspace/manufacturing/bom/${bomId}`);
        } catch (error) {
            if (error.name !== 'ValidationError') {
                Message.error(error.message || "Failed to update BOM");
            }
        } finally {
            setLoading(false);
        }
    };

    const finishedItemColumns = (remove) => [
        {
            title: "Item Name",
            dataIndex: "itemId",
            render: (text, record, index) => (
                <FormItem field={`finishedItems[${index}].itemId`} rules={[{ required: true }]} noStyle>
                    <Select placeholder="Select Item" showSearch allowClear
                        onChange={(val) => onItemSelected(val, index, 'finishedItems')}
                        filterOption={(inputValue, option) =>
                            option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}>
                        {items.filter(i => i.itemType === 1).sort((a, b) => a.itemName.localeCompare(b.itemName)).map(item => (
                            <Select.Option key={item.id} value={item.id}>{item.itemName}</Select.Option>
                        ))}
                    </Select>
                </FormItem>
            )
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (text, record, index) => (
                <FormItem field={`finishedItems[${index}].description`} noStyle>
                    <Input placeholder="Description" />
                </FormItem>
            )
        },
        {
            title: "Qty",
            dataIndex: "quantity",
            width: 100,
            render: (text, record, index) => (
                <FormItem field={`finishedItems[${index}].quantity`} rules={[{ required: true }]} noStyle>
                    <InputNumber min={0} />
                </FormItem>
            )
        },
        {
            title: "UOM",
            dataIndex: "uom",
            width: 120,
            render: (text, record, index) => (
                <FormItem field={`finishedItems[${index}].uom`} noStyle>
                    <Input disabled />
                </FormItem>
            )
        },
        {
            title: "Value Allocation",
            dataIndex: "valueAllocation",
            width: 150,
            render: (text, record, index) => (
                <FormItem field={`finishedItems[${index}].valueAllocation`} noStyle>
                    <InputNumber suffix="%" min={0} max={100} />
                </FormItem>
            )
        },
        {
            title: "Action",
            width: 80,
            render: (text, record, index) => (
                <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)} />
            )
        }
    ];

    const rawItemColumns = (remove) => [
        {
            title: "Item Name",
            dataIndex: "itemId",
            render: (text, record, index) => (
                <FormItem field={`rawItems[${index}].itemId`} rules={[{ required: true }]} noStyle>
                    <Select placeholder="Select Item" showSearch allowClear
                        onChange={(val) => onItemSelected(val, index, 'rawItems')}
                        filterOption={(inputValue, option) =>
                            option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}>
                        {items.sort((a, b) => a.itemName.localeCompare(b.itemName)).map(item => (
                            <Select.Option key={item.id} value={item.id}>{item.itemName}</Select.Option>
                        ))}
                    </Select>
                </FormItem>
            )
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (text, record, index) => (
                <FormItem field={`rawItems[${index}].description`} noStyle>
                    <Input placeholder="Description" />
                </FormItem>
            )
        },
        {
            title: "Qty",
            dataIndex: "quantity",
            width: 100,
            render: (text, record, index) => (
                <FormItem field={`rawItems[${index}].quantity`} rules={[{ required: true }]} noStyle>
                    <InputNumber min={0} />
                </FormItem>
            )
        },
        {
            title: "UOM",
            dataIndex: "uom",
            width: 150,
            render: (text, record, index) => (
                <FormItem field={`rawItems[${index}].uom`} noStyle>
                    <Input disabled />
                </FormItem>
            )
        },
        {
            title: "Action",
            width: 80,
            render: (text, record, index) => (
                <Button type="text" status="danger" icon={<IconDelete />} onClick={() => remove(index)} />
            )
        }
    ];

    return (
        <Card title="Edit Bill of Material" extra={
            <Space>
                <Button type="primary" onClick={onSubmit} loading={loading}>Save</Button>
                <Button onClick={() => navigate(`/workspace/manufacturing/bom/${bomId}`)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <Row gutter={24}>
                    <Col span={6}>
                        <FormItem label="BOM Name:" field="bomName" rules={[{ required: true, message: 'BOM Name is required' }]}>
                            <Input placeholder="BOM Name" />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Description:" field="description">
                            <Input placeholder="Description" />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Waste Factor:" field="wasteFactor" rules={[{ required: true, message: 'Waste Factor is required' }]}>
                            <InputNumber suffix="%" min={0} max={100} />
                        </FormItem>
                    </Col>
                    <Col span={6}>
                        <FormItem label="Status:" field="status">
                            <Select placeholder="Status">
                                <Select.Option value={Status.Active}>Active</Select.Option>
                                <Select.Option value={Status.Inactive}>Inactive</Select.Option>
                            </Select>
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={6}>
                        <FormItem label="Date Created:" field="dateCreated">
                            <Input disabled />
                        </FormItem>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <FormItem label="Notes:" field="notes" labelCol={{ span: 2 }} wrapperCol={{ span: 22 }}>
                            <Input.TextArea placeholder="Notes" autoSize={{ minRows: 2 }} />
                        </FormItem>
                    </Col>
                </Row>

                <Divider />

                <Form.List field="finishedItems">
                    {(fields, { add, remove }) => (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <Space align="center">
                                    <Title heading={6} style={{ margin: 0 }}>Finished Item</Title>
                                    <Button type="text" icon={<IconPlus />} onClick={() => {
                                        const currentItems = form.getFieldValue("finishedItems") || [];
                                        add(currentItems.length === 0 ? { valueAllocation: 100 } : {});
                                    }}>
                                        Add Finished Item
                                    </Button>
                                </Space>
                            </div>
                            <Table
                                data={fields}
                                columns={finishedItemColumns(remove)}
                                pagination={false}
                            />
                        </>
                    )}
                </Form.List>

                <Divider />

                <Form.List field="rawItems">
                    {(fields, { add, remove }) => (
                        <>
                            <div style={{ marginBottom: 16, marginTop: 24 }}>
                                <Space align="center">
                                    <Title heading={6} style={{ margin: 0 }}>Raw Item</Title>
                                    <Button type="text" icon={<IconPlus />} onClick={() => add()}>
                                        Add Raw Item
                                    </Button>
                                </Space>
                            </div>
                            <Table
                                data={fields}
                                columns={rawItemColumns(remove)}
                                pagination={false}
                            />
                        </>
                    )}
                </Form.List>
            </Form>
        </Card>
    );
};
